import React, { Suspense, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Canvas } from "@react-three/fiber";
import { LightV1, RenderLayout, createDefaultMaterial, CutPlane, JoinType, JoinPlane, JoinVariant } from '@3dcalc/3dpClient'
import { apiDemo } from "../../api/apiDemo";
import { OrbitControls } from "@react-three/drei";

const SLIDERS = {
    width:  { min: 2, max: 6,   step: 0.5,  defaultVal: 3.0 },
    length: { min: 2, max: 6,   step: 0.5,  defaultVal: 3.0 },
    height: { min: 2, max: 3,   step: 0.2,  defaultVal: 2.0 },
    shift:  { min: 0, max: 0.5, step: 0.02, defaultVal: 0   },
}

const PreviewScene = ({ width, length, height, shift }) => {
    const group = useRef();
    const [struct, setStruct] = useState([])

    useEffect(() => { resizeDemo() }, [width, length, height, shift])

    const resizeDemo = async () => {
        const paramBoard1 = {w: 0.2, h: 0.2, l: height}
        const paramBoard2 = {w: 0.2, h: 0.2, l: width + 0.7}
        const paramBoard3 = {w: 0.2, h: 0.3, l: length}
        const gash = 0.02
        const gashCut = paramBoard1.w / 2
        const _struct = []

        {
            const paramsGetSimpleBoard = { modeBuild: true, shift: {back: 0, front: 0}, sizeBoard: paramBoard1, cuts: [] }
            const rs = { vertices: [], indices: [], uvs: [], material: [], elements: [], group: '' }
            const resultGetSimpleBoard = await apiDemo.getSimpleBoard.build(paramsGetSimpleBoard)
            const pillar = resultGetSimpleBoard.mesh
            rs.material.push(createDefaultMaterial())
            rs.uvs.push(pillar.uvs); const numUvs = rs.uvs.length - 1
            rs.indices.push(pillar.indices); const numIndices = rs.indices.length - 1
            rs.vertices.push(pillar.vertices); const numVertices = rs.vertices.length - 1
            const nameGroup = 'K'; rs.group = nameGroup
            const posL = [[length/2, height/2, width/2], [-length/2, height/2, width/2]]
            const posR = [[length/2, height/2, -width/2], [-length/2, height/2, -width/2]]
            for (let i = 0; i < 2; i++) {
                rs.elements.push({ indices: numIndices, name: `${nameGroup}-L-${i}`, nameGroup, position: posL[i], rotation: [0,0,0], uvs: numUvs, vertices: numVertices, material: 0 })
                rs.elements.push({ indices: numIndices, name: `${nameGroup}-R-${i}`, nameGroup, position: posR[i], rotation: [0,0,0], uvs: numUvs, vertices: numVertices, material: 0 })
            }
            _struct.push(rs)
        }

        {
            const makeComplexCuts = (plane) => [
                { point1: [0.35,0], point2: [0.35,0], cuts: [{gash,width:paramBoard1.w,plane:CutPlane.Top},{gash:gashCut,width:paramBoard1.w,plane}] },
                { point1: [paramBoard2.l-0.35,0], point2: [paramBoard2.l-0.35,0], cuts: [{gash,width:paramBoard1.w,plane:CutPlane.Top},{gash:gashCut,width:paramBoard1.w,plane}] },
            ]
            const rsL = { vertices:[], indices:[], uvs:[], material:[], elements:[], group:'H' }
            const rsR = { vertices:[], indices:[], uvs:[], material:[], elements:[], group:'H' }
            const hL = (await apiDemo.getSimpleBoard.build({ modeBuild:true, shift:{back:0,front:0}, sizeBoard:paramBoard2, complexCuts:makeComplexCuts(CutPlane.Left) })).mesh
            const hR = (await apiDemo.getSimpleBoard.build({ modeBuild:true, shift:{back:0,front:0}, sizeBoard:paramBoard2, complexCuts:makeComplexCuts(CutPlane.Right) })).mesh
            const nameGroup = 'H'
            rsL.material.push(createDefaultMaterial())
            rsL.uvs.push(hL.uvs); const nuL = rsL.uvs.length-1
            rsL.indices.push(hL.indices); const niL = rsL.indices.length-1
            rsL.vertices.push(hL.vertices); const nvL = rsL.vertices.length-1
            rsL.elements.push({ indices:niL, name:`${nameGroup}-L`, nameGroup, position:[length/2+shift/2, height+paramBoard2.w/2-0.02, 0], rotation:[Math.PI/2,0,0], uvs:nuL, vertices:nvL, material:0 })
            _struct.push(rsL)
            rsR.material.push(createDefaultMaterial())
            rsR.uvs.push(hR.uvs); const nuR = rsR.uvs.length-1
            rsR.indices.push(hR.indices); const niR = rsR.indices.length-1
            rsR.vertices.push(hR.vertices); const nvR = rsR.vertices.length-1
            rsR.elements.push({ indices:niR, name:`${nameGroup}-R`, nameGroup, position:[-length/2-shift/2, height+paramBoard2.w/2-0.02, 0], rotation:[Math.PI/2,0,0], uvs:nuR, vertices:nvR, material:0 })
            _struct.push(rsR)
        }

        {
            const joinS = { gash, width:paramBoard2.h/2, angle:20, indent:paramBoard2.w, joinType:JoinType.Start, joinPlane:JoinPlane.Bottom, variant:JoinVariant.GashAndAngle }
            const joinE = { gash, width:paramBoard2.h/2, angle:20, indent:paramBoard2.w, joinType:JoinType.End,   joinPlane:JoinPlane.Bottom, variant:JoinVariant.GashAndAngle }
            const rs = { vertices:[], indices:[], uvs:[], material:[], elements:[], group:'F' }
            const pillar = (await apiDemo.getSimpleBoard.build({ modeBuild:true, shift:{back:0,front:0}, sizeBoard:paramBoard3, cuts:[], joinStart:joinS, joinEnd:joinE })).mesh
            rs.material.push(createDefaultMaterial())
            rs.uvs.push(pillar.uvs); const nu = rs.uvs.length-1
            rs.indices.push(pillar.indices); const ni = rs.indices.length-1
            rs.vertices.push(pillar.vertices); const nv = rs.vertices.length-1
            const nameGroup = 'F'; rs.group = nameGroup
            rs.elements.push({ indices:ni, name:`${nameGroup}-L`, nameGroup, position:[0, height+paramBoard3.h/2-gash+shift, width/2],  rotation:[Math.PI/2,0,Math.PI/2], uvs:nu, vertices:nv, material:0 })
            rs.elements.push({ indices:ni, name:`${nameGroup}-R`, nameGroup, position:[0, height+paramBoard3.h/2-gash+shift, -width/2], rotation:[Math.PI/2,0,Math.PI/2], uvs:nu, vertices:nv, material:0 })
            _struct.push(rs)
        }

        setStruct(_struct)
    }

    return (
        <group ref={group} position={[0, -1.4, 0]} rotation={[0, Math.PI/1.5, 0]}>
            <RenderLayout struct={struct} palette={[]} onPaletteChange={() => {}} onColorChange={() => {}} />
        </group>
    )
}

const DemoViewer = ({ width, length, height, shift }) => (
    <div className="w-full h-full min-h-72 bg-gray-950 rounded-2xl border border-gray-800 relative overflow-hidden">
        <div className="absolute inset-0 opacity-15 pointer-events-none" style={{
            backgroundImage: 'linear-gradient(rgba(16,185,129,.5) 1px, transparent 1px),linear-gradient(90deg, rgba(16,185,129,.5) 1px, transparent 1px)',
            backgroundSize: '28px 28px',
            transform: 'perspective(500px) rotateX(58deg) scaleX(1.6)',
            transformOrigin: 'bottom center',
        }} />
        <Suspense fallback={<div className="absolute inset-0 flex items-center justify-center"><span className="text-white/20 text-sm tracking-widest uppercase">Загрузка…</span></div>}>
            <Canvas style={{ position:'absolute', inset:0, width:'100%', height:'100%' }} camera={{ position:[8,-1,2], fov:45 }} gl={{ antialias:true }}>
                <LightV1 />
                <PreviewScene width={width} height={height} length={length} shift={shift} />
                <OrbitControls />
            </Canvas>
        </Suspense>
    </div>
)

const Slider = ({ label, value, min, max, step, unit, onChange }) => (
    <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-300">{label}</span>
            <span className="text-sm font-mono font-bold text-emerald-400 tabular-nums">
                {value.toFixed(1)}&thinsp;{unit}
            </span>
        </div>
        <input type="range" min={min} max={max} step={step} value={value}
               onChange={e => onChange(parseFloat(e.target.value))}
               className="w-full h-1.5 rounded-full cursor-pointer appearance-none bg-gray-700 accent-emerald-500 focus:outline-none"
        />
        <div className="flex justify-between text-xs text-gray-600">
            <span>{min}&thinsp;{unit}</span>
            <span>{max}&thinsp;{unit}</span>
        </div>
    </div>
)

const DemoSection = () => {
    const { t } = useTranslation()

    const [width,  setWidth]  = useState(SLIDERS.width.defaultVal)
    const [length, setLength] = useState(SLIDERS.length.defaultVal)
    const [height, setHeight] = useState(SLIDERS.height.defaultVal)
    const [shift,  setShift]  = useState(SLIDERS.shift.defaultVal)

    const unit = t('demo.m')

    const sliders = [
        { key:'width',  label: t('demo.width'),  value: width,  set: setWidth,  ...SLIDERS.width  },
        { key:'length', label: t('demo.length'), value: length, set: setLength, ...SLIDERS.length },
        { key:'height', label: t('demo.height'), value: height, set: setHeight, ...SLIDERS.height },
        { key:'shift',  label: t('demo.shift'),  value: shift,  set: setShift,  ...SLIDERS.shift  },
    ]

    return (
        <section id="demo" className="py-24 bg-gray-900">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="text-center mb-14">
                    <span className="inline-block text-emerald-500 text-xs font-bold tracking-widest uppercase mb-3">
                        {t('demo.label')}
                    </span>
                    <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">{t('demo.title')}</h2>
                    <p className="text-gray-400 text-lg max-w-xl mx-auto">{t('demo.subtitle')}</p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 items-stretch">
                    <div className="lg:col-span-3 min-h-72">
                        <DemoViewer width={width} length={length} height={height} shift={shift} />
                    </div>

                    <div className="lg:col-span-2 bg-gray-800/50 border border-gray-700/50 rounded-2xl p-6 flex flex-col gap-6">
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-widest">
                            {t('demo.configuration')}
                        </p>

                        {sliders.map(({ key, label, value, set, min, max, step }) => (
                            <Slider key={key} label={label} value={value}
                                    min={min} max={max} step={step}
                                    unit={unit}
                                    onChange={set}
                            />
                        ))}

                        <div className="mt-1 rounded-xl bg-gray-900/70 border border-gray-700/40 p-4">
                            <p className="text-xs text-gray-500 uppercase tracking-wider mb-3">{t('demo.summary')}</p>
                            <div className="grid grid-cols-4 gap-2 text-center">
                                {sliders.map(({ key, label, value }) => (
                                    <div key={key}>
                                        <p className="text-xl font-mono font-bold text-emerald-400 tabular-nums">
                                            {value.toFixed(1)}
                                        </p>
                                        <p className="text-xs text-gray-600 mt-0.5">{label}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    )
}

export default DemoSection
